import React, { useMemo, useState, useEffect } from 'react';
import { Card, Typography, Input, Row, Col, Space, Select, Tag, Spin, message } from 'antd';
import { IoRefreshCircle } from 'react-icons/io5';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import StandardTable from '../../../../components/common/StandardTable';
import QaqcPipeWeldingDataResultForm from './QaqcPipeWeldingDataResultForm';
import { useGetQaqcPipeWeldingdataLog, useGetQaqcPipeWeldingdataResult } from '../../../../hooks/genericHooks';
import type { QaqcPipeWeldingdataResult, QaqcPipeWeldingdataLog } from '../../../../types';
import { fetchSqlDataWithQuery, fetchSqlDataStreaming } from '../../../../services/api';
import dayjs from 'dayjs';
import VirtualList from 'rc-virtual-list';
import Button from '../../../../components/common/Button';

const { Title } = Typography;

// React-icons wrapper to satisfy TS in JSX (same pattern as AdmBldDashboard)
const RRefresh: React.FC<any> = (props) =>
  React.createElement(IoRefreshCircle as unknown as React.ComponentType<any>, props) as React.ReactElement;

// Icon sizing (change here to adjust globally for this page)
const QaqcPipeWeldingDataResultList: React.FC = () => {
  const [list, setList] = useState<QaqcPipeWeldingdataResult[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { data: logResp, isLoading: loadingLog, isFetching: fetchingLog, refetch: refetchLog } = useGetQaqcPipeWeldingdataLog() as any;
  const weldingLogList: QaqcPipeWeldingdataLog[] = logResp?.data ?? [];
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<QaqcPipeWeldingdataResult | null>(null);
  const [uniqueness, setUniqueness] = useState<'all' | 'unique' | 'non-unique'>('all');

  const [loadingAllResults, setLoadingAllResults] = useState<boolean>(false);
  const [loadedCount, setLoadedCount] = useState<number>(0);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<{
    fluid?: string[];
    section?: string[]; // area
    line_no?: string[];
    spool_id?: string[];
    revision_no?: string[];
    serviceclass_name?: string[];
  }>({});

  const cascadeOrder: (keyof QaqcPipeWeldingdataLog)[] = [
    'fluid',
    'section',
    'line_no',
    'spool_id',
    'revision_no',
    'serviceclass_name',
  ];

  // Virtualized tbody wrapper using rc-virtual-list
  const itemHeight = 40;
  const viewportHeight = 600;
  const VirtualBody = (rawData: any[], { children }: any) => {
    const rows = Array.isArray(children) ? children : (children ? [children] : []);
    return (
      <VirtualList
        data={rows}
        height={viewportHeight}
        itemHeight={itemHeight}
        itemKey={(child: any) => (child?.key as React.Key)}
        component="tbody"
      >
        {(child: any) => child}
      </VirtualList>
    );
  };

  // Apply filters on welding log list
  const applyLogFilters = (
    dataIn: QaqcPipeWeldingdataLog[],
    opts?: { excludeField?: keyof QaqcPipeWeldingdataLog }
  ) => {
    // Special rule: if only 'section' filter is active, match rows whose weldingdata_id contains the section value (partial match)
    const hasFluid = !!(filters as any).fluid?.length;
    const hasSection = !!(filters as any).section?.length;
    const hasLine = !!(filters as any).line_no?.length;
    const hasSpool = !!(filters as any).spool_id?.length;
    const hasRev = !!(filters as any).revision_no?.length;
    const hasSvc = !!(filters as any).serviceclass_name?.length;
    const onlySectionActive = hasSection && !hasFluid && !hasLine && !hasSpool && !hasRev && !hasSvc;

    if (onlySectionActive && opts?.excludeField !== 'section') {
      const sectVals = (filters as any).section as string[];
      return dataIn.filter(row => {
        const wid = String((row as any).weldingdata_id ?? '').toLowerCase();
        return sectVals.some(v => wid.includes(String(v).toLowerCase()));
      });
    }

    return dataIn.filter(row => {
      for (const k of cascadeOrder) {
        if (opts?.excludeField === k) continue;
        const vals = (filters as any)[k] as string[] | undefined;
        if (vals && vals.length > 0) {
          const cell = String((row as any)[k] ?? '').toLowerCase();
          const matched = k === 'line_no'
            ? vals.some(v => String(v).toLowerCase().trim() !== '' && cell.includes(String(v).toLowerCase()))
            : vals.some(v => String(v).toLowerCase() === cell);
          if (!matched) return false;
        }
      }
      return true;
    });
  };

  // Build filter options from welding log list
  const buildWeldingLogOptions = (field: keyof QaqcPipeWeldingdataLog) => {
    const s = search.trim().toLowerCase();
    const searchFiltered = s
      ? weldingLogList.filter(r => {
          const hay = `${r.weldingdata_id} ${r.line_no}`.toLowerCase();
          return hay.includes(s);
        })
      : weldingLogList;
    const base = applyLogFilters(searchFiltered, { excludeField: field });
    const set = new Set<string>();
    base.forEach(r => {
      const v = (r as any)[field];
      if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set).sort((a,b) => a.localeCompare(b)).map(v => ({ label: v, value: v }));
  };

  // Compute a stable key for weldingdata_ids after applying welding log filters
  const weldingIdKey = useMemo(() => {
    const base = applyLogFilters(weldingLogList);
    const ids = new Set<string>();
    base.forEach(r => { if (r.weldingdata_id) ids.add(String(r.weldingdata_id)); });
    return Array.from(ids).sort().join(',');
  }, [weldingLogList, filters]);

  // Notify user when filters become ready after initial load
  const [filtersReadyAnnounced, setFiltersReadyAnnounced] = useState(false);
  useEffect(() => {
    if (!loadingLog && weldingLogList.length > 0 && !filtersReadyAnnounced) {
      try { message.success('Filters ready'); } catch {}
      setFiltersReadyAnnounced(true);
    }
  }, [loadingLog, weldingLogList, filtersReadyAnnounced]);

  // Build a Set from the stable key for quick lookups elsewhere
  const filteredWeldingDataIds = useMemo(() => {
    if (!weldingIdKey) return new Set<string>();
    return new Set<string>(weldingIdKey.split(','));
  }, [weldingIdKey]);

  // Fetch results from backend based on filtered weldingdata_id set
  useEffect(() => {
    if (showAll) return; // bypass filtered fetching in show-all mode
    const hasAnyFilter = Object.values(filters || {}).some((v: any) => Array.isArray(v) && v.length > 0);
    let cancelled = false;
    const fetchResults = async () => {
      // If no IDs, clear list and stop
      // console.debug('filtered ids:', filteredWeldingDataIds.size);
      // Abort when no filters and no search (initial/cleared state)
      if (!hasAnyFilter && !search.trim()) {
        if (!cancelled && list.length !== 0) setList([]);
        return;
      }
      if (!filteredWeldingDataIds || filteredWeldingDataIds.size === 0) {
        if (!cancelled && list.length !== 0) setList([]);
        return;
      }
      if (!cancelled) setIsLoading(true);
      try {
        // Single query with all IDs
        const allIds = Array.from(filteredWeldingDataIds);
        const inList = allIds
          .map(v => `'${String(v).replace(/'/g, "''")}'`)
          .join(',');
        const query = `SELECT * FROM qaqc_pipe_weldingdata_result WHERE weldingdata_id IN (${inList}) ORDER BY weldingdata_id, weld_no_rev`;
        const resp = await fetchSqlDataWithQuery(query);
        const arr = Array.isArray(resp) ? resp : resp?.data ?? [];
        const safeArr = Array.isArray(arr) ? arr : [];
        // De-duplicate by id to be safe, then stable sort
        const byId = new Map<number, any>();
        safeArr.forEach((r: any) => {
          if (r && r.id != null) byId.set(Number(r.id), r);
        });
        const finalArr = Array.from(byId.values()) as QaqcPipeWeldingdataResult[];
        finalArr.sort((a: any, b: any) => {
          const a1 = String(a.weldingdata_id || '');
          const b1 = String(b.weldingdata_id || '');
          if (a1 === b1) return Number(a.weld_no_rev || 0) - Number(b.weld_no_rev || 0);
          return a1.localeCompare(b1);
        });
        if (!cancelled) setList(finalArr);
      } catch (e) {
        if (!cancelled) setList([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchResults();
    return () => {
      cancelled = true;
    };
  }, [weldingIdKey, filters, search, showAll]);

  useEffect(() => {
    if (!showAll) return;
    setLoadingAllResults(true);
    setList([]);
    const controller = fetchSqlDataStreaming({
      table: 'qaqc_pipe_weldingdata_result',
      orderBy: ['weldingdata_id ASC', 'weld_no_rev ASC'],
      batchSize: 25000,
      flushMs: 240,
      onBatch: (rows: any[]) => {
        setList(prev => prev.concat(rows as any));
      },
      onDone: () => {
        setLoadingAllResults(false);
      },
      onError: () => {
        setLoadingAllResults(false);
      },
    });
    return () => {
      try { controller.abort(); } catch {}
    };
  }, [showAll]);

  // Open edit form (double-click row)
  const openEditForm = (record: QaqcPipeWeldingdataResult) => {
    setEditingRecord(record);
    setFormOpen(true);
  };

  const closeEditForm = () => {
    setFormOpen(false);
    setEditingRecord(null);
  };

  // After update, refresh the edited row from backend and merge into list
  const handleUpdated = async () => {
    const id = (editingRecord as any)?.id;
    if (!id) return;
    try {
      const resp = await fetchSqlDataWithQuery(`SELECT * FROM qaqc_pipe_weldingdata_result WHERE id = ${Number(id)} LIMIT 1`);
      const updated = Array.isArray(resp) ? resp[0] : (resp?.data?.[0] ?? null);
      if (updated) {
        setList(prev => prev.map(r => (r as any).id === updated.id ? { ...(r as any), ...updated } : r));
      }
    } catch {}
  };

  // Exit show-all mode when user types or sets any filter
  useEffect(() => {
    const hasAnyFilter = Object.values(filters || {}).some((v: any) => Array.isArray(v) && v.length > 0);
    if (hasAnyFilter || search.trim()) {
      if (showAll) setShowAll(false);
    }
  }, [filters, search, showAll]);

  // Final filtered results list: by weldingdata_id set and search text
  const filteredData = useMemo(() => {
    const s = search.trim().toLowerCase();
    let base = showAll ? list : list.filter(r => filteredWeldingDataIds.has(String(r.weldingdata_id)));
    // Apply uniqueness over current base set
    if (uniqueness !== 'all') {
      const counts = new Map<string, number>();
      for (const r of base as any[]) {
        const key = String((r as any).weldingdata_id ?? '');
        counts.set(key, (counts.get(key) || 0) + 1);
      }
      const wantUnique = uniqueness === 'unique';
      base = base.filter((r: any) => (wantUnique ? (counts.get(String(r.weldingdata_id)) || 0) === 1 : (counts.get(String(r.weldingdata_id)) || 0) > 1));
    }
    if (!s) return base;
    return base.filter(r => {
      const hay = `${r.weldingdata_id} ${r.bc_report_no ?? ''} ${r.vc_report_no ?? ''} ${r.pt_report_no ?? ''} ${r.rt_report_no ?? ''} ${r.mt_report_no ?? ''} ${r.ut_report_no ?? ''}`.toLowerCase();
      return hay.includes(s);
    });
  }, [list, filteredWeldingDataIds, search, showAll, uniqueness]);

  const columns: ColumnsType<QaqcPipeWeldingdataResult> = [
    // Identity
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Welding Data ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 200 },
    { title: 'Rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 60 },
    // Component info
    { title: 'Comp.1', dataIndex: 'component_name1', key: 'component_name1', width: 160 },
    { title: 'Heat.1', dataIndex: 'heat_no1', key: 'heat_no1', width: 100 },
    { title: 'Comp.2', dataIndex: 'component_name2', key: 'component_name2', width: 160 },
    { title: 'Heat.2', dataIndex: 'heat_no2', key: 'heat_no2', width: 100 },
    { title: 'SubItem 1', dataIndex: 'subitemcode1', key: 'subitemcode1', width: 120 },
    { title: 'SubItem 2', dataIndex: 'subitemcode2', key: 'subitemcode2', width: 120 },
    // BC group: report, date, inspector
    { title: 'BC Rep', dataIndex: 'bc_report_no', key: 'bc_report_no', width: 140 },
    { title: 'BC Date', dataIndex: 'bc_date', key: 'bc_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'BC Insp', dataIndex: 'bc_inspector', key: 'bc_inspector', width: 140 },
    // VC group
    { title: 'VC Rep', dataIndex: 'vc_report_no', key: 'vc_report_no', width: 140 },
    { title: 'VC Date', dataIndex: 'vc_date', key: 'vc_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'VC Insp', dataIndex: 'vc_inspector', key: 'vc_inspector', width: 140 },
    // PMI group
    { title: 'PMI Rep', dataIndex: 'pmi_report_no', key: 'pmi_report_no', width: 140 },
    { title: 'PMI Date', dataIndex: 'pmi_date', key: 'pmi_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'PMI Insp', dataIndex: 'pmi_inspector', key: 'pmi_inspector', width: 140 },
    // PT group
    { title: 'PT Rep', dataIndex: 'pt_report_no', key: 'pt_report_no', width: 140 },
    { title: 'PT Date', dataIndex: 'pt_date', key: 'pt_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'PT Insp', dataIndex: 'pt_inspector', key: 'pt_inspector', width: 140 },
    { title: 'PT', dataIndex: 'pt_result', key: 'pt_result', width: 70, render: (v) => (v === true ? <Tag color="green">Pass</Tag> : v === false ? <Tag color="red">Fail</Tag> : '-') },
    // RT group
    { title: 'RT Rep', dataIndex: 'rt_report_no', key: 'rt_report_no', width: 140 },
    { title: 'RT Date', dataIndex: 'rt_date', key: 'rt_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'RT Insp', dataIndex: 'rt_inspector', key: 'rt_inspector', width: 140 },
    { title: 'RT', dataIndex: 'rt_result', key: 'rt_result', width: 70, render: (v) => (v === true ? <Tag color="green">Pass</Tag> : v === false ? <Tag color="red">Fail</Tag> : '-') },
    // MT group
    { title: 'MT Rep', dataIndex: 'mt_report_no', key: 'mt_report_no', width: 140 },
    { title: 'MT Date', dataIndex: 'mt_date', key: 'mt_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'MT Insp', dataIndex: 'mt_inspector', key: 'mt_inspector', width: 140 },
    { title: 'MT', dataIndex: 'mt_result', key: 'mt_result', width: 70, render: (v) => (v === true ? <Tag color="green">Pass</Tag> : v === false ? <Tag color="red">Fail</Tag> : '-') },
    // UT group
    { title: 'UT Rep', dataIndex: 'ut_report_no', key: 'ut_report_no', width: 140 },
    { title: 'UT Date', dataIndex: 'ut_date', key: 'ut_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'UT Insp', dataIndex: 'ut_inspector', key: 'ut_inspector', width: 140 },
    { title: 'UT', dataIndex: 'ut_result', key: 'ut_result', width: 70, render: (v) => (v === true ? <Tag color="green">Pass</Tag> : v === false ? <Tag color="red">Fail</Tag> : '-') },
    // PWHT group
    { title: 'PWHT Rep', dataIndex: 'pwht_report_no', key: 'pwht_report_no', width: 140 },
    { title: 'PWHT Date', dataIndex: 'pwht_date', key: 'pwht_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'PWHT Insp', dataIndex: 'pwht_inspector', key: 'pwht_inspector', width: 140 },
    { title: 'PWHT Hardness', dataIndex: 'pwht_hardness', key: 'pwht_hardness', width: 120 },
    // Welding operations
    { title: 'FLW Date', dataIndex: 'flw_date', key: 'flw_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'FLW Welder', dataIndex: 'flw_welder', key: 'flw_welder', width: 160 },
    { title: 'FW Date', dataIndex: 'fw_date', key: 'fw_date', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'FW Welder', dataIndex: 'fw_welder', key: 'fw_welder', width: 160 },
    // Other
    { title: 'HT Rev Date', dataIndex: 'ht_rev_date', key: 'ht_rev_date', width: 120, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'Updated At', dataIndex: 'updated_at', key: 'updated_at', width: 160, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-') },
    { title: 'Updated By', dataIndex: 'updated_by', key: 'updated_by', width: 160 },
  ];

  // Controlled pagination to avoid stale pages after filtering
  const [pageState, setPageState] = useState<{ current: number; pageSize: number }>({ current: 1, pageSize: 20 });
  useEffect(() => {
    setPageState(prev => ({ ...prev, current: 1 }));
  }, [search, filters]);

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col><Title level={4} style={{ margin: 0 }}>QA/QC Piping • Welding Data Results</Title></Col>
        <Col>
          <Space>
            <Input
              allowClear
              placeholder="Search id/report no"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 360 }}
            />
            <Button size="small" onClick={() => { setFilters({}); setSearch(''); setUniqueness('all'); }}>Reset</Button>
            <Button
              size="small"
              onClick={() => { if (!showAll) refetchLog(); }}
              disabled={showAll && loadingAllResults}
              style={{ backgroundColor: '#faad14', borderColor: '#faad14', color: '#000'}}
            >
              <RRefresh size={20} style={{ marginRight: 2 }} />
              Filters
            </Button>
            <Button
              size="small"
              onClick={() => {
                setFilters({});
                setSearch('');
                setShowAll(true);
                setPageState(prev => ({ ...prev, current: 1 }));
              }}
            >
              Show All
            </Button>
          </Space>
        </Col>
      </Row>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} size={8}>
        {(loadingLog || fetchingLog) && (
          <Row>
            <Col>
              <Space size={6}>
                <Spin size="small" />
                <span>Loading filters… Please wait</span>
              </Space>
            </Col>
          </Row>
        )}
        <Row gutter={[8,8]}>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildWeldingLogOptions('fluid')}
              placeholder="Fluid"
              value={filters.fluid}
              onChange={(v) => setFilters(prev => ({ ...prev, fluid: (v && v.length ? v : undefined) }))}
              disabled={loadingLog}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={3}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildWeldingLogOptions('section')}
              placeholder="Area (Section)"
              value={filters.section}
              onChange={(v) => setFilters(prev => ({ ...prev, section: (v && v.length ? v : undefined) }))}
              disabled={loadingLog}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={8}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildWeldingLogOptions('line_no')}
              placeholder="Line No"
              value={filters.line_no}
              onChange={(v) => setFilters(prev => ({ ...prev, line_no: (v && v.length ? v : undefined) }))}
              disabled={loadingLog}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={3}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (String(option?.label ?? '').toLowerCase()).startsWith(input.toLowerCase())
              }
              options={buildWeldingLogOptions('spool_id')}
              placeholder="Spool"
              value={filters.spool_id}
              onChange={(v) => setFilters(prev => ({ ...prev, spool_id: (v && v.length ? v : undefined) }))}
              disabled={loadingLog}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildWeldingLogOptions('revision_no')}
              placeholder="Rev"
              value={filters.revision_no}
              onChange={(v) => setFilters(prev => ({ ...prev, revision_no: (v && v.length ? v : undefined) }))}
              disabled={loadingLog}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildWeldingLogOptions('serviceclass_name')}
              placeholder="S.Class"
              value={filters.serviceclass_name}
              onChange={(v) => setFilters(prev => ({ ...prev, serviceclass_name: (v && v.length ? v : undefined) }))}
              disabled={loadingLog}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <Select
              options={[
                { label: 'All', value: 'all' },
                { label: 'Unique', value: 'unique' },
                { label: 'Non-unique', value: 'non-unique' },
              ]}
              placeholder="Uniqueness"
              value={uniqueness}
              onChange={(v) => setUniqueness(v)}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Space>

      <StandardTable<QaqcPipeWeldingdataResult>
        loading={isLoading || (showAll && loadingAllResults)}
        dataSource={filteredData}
        columns={columns as any}
        rowKey="id"
        bordered
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys, columnWidth: 8 }}
        pagination={{ current: pageState.current, pageSize: pageState.pageSize, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] }}
        onChange={(p: any) => {
          if (!p) return;
          const nextCurrent = p.current || 1;
          const nextSize = p.pageSize || pageState.pageSize;
          if (nextCurrent !== pageState.current || nextSize !== pageState.pageSize) {
            setPageState({ current: nextCurrent, pageSize: nextSize });
          }
        }}
        onRow={(record) => ({
          onDoubleClick: () => openEditForm(record as QaqcPipeWeldingdataResult),
        })}
        components={{ body: VirtualBody as any }}
      />

      <QaqcPipeWeldingDataResultForm
        open={formOpen}
        onClose={closeEditForm}
        initialValues={editingRecord}
        onUpdated={handleUpdated}
      />
    </Card>
  );
};

export default QaqcPipeWeldingDataResultList;
